import { Actions, reducer } from './reducer';
import {
  HistorialCitasState,
  HistorialCitasFormParams,
  CitaProps,
} from 'utils/interfaces';
import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { API } from 'utils';
import { FilterBy } from '../../../utils/interfaces/index';

export const historialCitasInitialState: HistorialCitasState = {
  view: 'form',
  isFormLoading: false,
  proximasCitas: [],
  previasCitas: [],
  filterBy: null,
  idCita: undefined,
  showCitaCanceladaModal: false,
  sort: 'desc',
  pagePrevias: 0,
  pageProximas: 0,
  perPage: 5,
  nombrePaciente: '',
};

interface ContextState extends HistorialCitasState {
  resetState: () => void;
  dispatch: React.Dispatch<Actions>;
  onFormSubmit: (params: HistorialCitasFormParams) => void;
  setFilterBy: (payload: FilterBy) => void;
  onCancelarCita: () => void;
  setPagePrevias: (page: number) => void;
  setPageProximas: (page: number) => void;
  resetPages: () => void;
  filteredProximasCitas: [CitaProps[], number];
  filteredPreviasCitas: [CitaProps[], number];
}

const filterGeneralFunc = (item: CitaProps) => !item.medico;
const filterSpecialistFunc = (item: CitaProps) => Boolean(item.medico);

const HistorialCitasContext = createContext({} as ContextState);

export function HistorialCitasProvider({
  children,
}: PropsWithChildren<ReactNode>) {
  const [state, dispatch] = useReducer(reducer, historialCitasInitialState);

  const resetState = () => {
    dispatch({ type: 'reset' });
  };

  const setFormLoading = (payload: boolean) => {
    dispatch({ type: 'setFormLoading', payload });
  };

  const resetPages = () => {
    dispatch({ type: 'resetPages' });
  };

  const setFilterBy = (payload: FilterBy) => {
    dispatch({ type: 'setFilterBy', payload });
    resetPages();
  };

  const filteredProximasCitas = useMemo<[CitaProps[], number]>(() => {
    const startElement = state.pagePrevias * state.perPage;
    const lastElement = startElement + state.perPage;
    if (!state.filterBy) return [
      state.proximasCitas.slice(startElement, lastElement),
      state.proximasCitas.length
    ];
    const condition =
      state.filterBy === 'especialista'
        ? filterSpecialistFunc
        : filterGeneralFunc;
    const result = state.proximasCitas.filter(condition);
    return [
      result.slice(startElement, lastElement),
      result.length,
    ];
  }, [state.filterBy, state.proximasCitas, state.sort, state.pageProximas]);

  const filteredPreviasCitas = useMemo<[CitaProps[], number]>(() => {
    const startElement = state.pagePrevias * state.perPage;
    const lastElement = startElement + state.perPage;
    if (!state.filterBy) return [
      state.previasCitas.slice(startElement, lastElement),
      state.previasCitas.length,
    ];
    const condition =
      state.filterBy === 'especialista'
        ? filterSpecialistFunc
        : filterGeneralFunc;
    const result = state.previasCitas.filter(condition);
    return [
      result.slice(startElement, lastElement),
      result.length,
    ];
  }, [state.filterBy, state.previasCitas, state.sort, state.pagePrevias]);


  const getCitas = async (params: HistorialCitasFormParams) => {
    interface GetCitasResponse {
      data: { citas: CitaProps[]; success: boolean; message: string, nombrePaciente: string };
    }
    try {
      const { data }: GetCitasResponse = await API.getCitas(params);
      if (data.success && data.message === '0') {
        dispatch({
          type: 'setCitas',
          payload: { citas: data.citas, nombrePaciente: data.nombrePaciente },
        });
      } else {
        dispatch({
          type: 'setStatusMessage',
          // prefijo hc para diferencial los messages de historial citas
          payload: `hc${data.message}`,
        });
        return false;
      }
    } catch (error) {
      console.error(error);
    }
    return true;
  };

  const onFormSubmit = async (params: HistorialCitasFormParams) => {
    setFormLoading(true);
    getCitas(params)
      .then((success) => {
        if (success) {
          dispatch({ type: 'setView', payload: 'response' });
          dispatch({ type: 'saveFormParams', payload: params });
        }
      })
      .finally(() => setFormLoading(false));
  };

  const onCancelarCita = async () => {
    try {
      const { data } = await API.cancelarCita(state.idCita as string);

      if (data.success) {
        const cancelSuccess = await getCitas(
          state.formParams as HistorialCitasFormParams
        );
        if (cancelSuccess) {
          dispatch({
            type: 'showCitaCanceladaModal',
            payload: true,
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const setPagePrevias = (page: number) => {
    dispatch({ type: 'setPagePrevias', payload: page });
  };

  const setPageProximas = (page: number) => {
    dispatch({ type: 'setPageProximas', payload: page });
  };

  const value = {
    ...state,
    dispatch,
    resetState,
    onFormSubmit,
    setFilterBy,
    onCancelarCita,
    setPageProximas,
    setPagePrevias,
    resetPages,
    filteredProximasCitas,
    filteredPreviasCitas,
  };

  return (
    <HistorialCitasContext.Provider value={value}>
      {children}
    </HistorialCitasContext.Provider>
  );
}

export function useHistorialCitas() {
  return useContext(HistorialCitasContext);
}
